/* import __COLOCATED_TEMPLATE__ from './live-person-connection.hbs'; */
/* RESPONSIBLE TEAM: team-rd-services */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'embercom/services/intl';
import { type TaskGenerator } from 'ember-concurrency';
import { post } from 'embercom/lib/ajax';
import type LivePersonConfiguration from 'embercom/models/standalone/live-person-configuration';
import type Token from 'embercom/models/custom-authentication/token';
import type RouterService from '@ember/routing/router-service';
import type Store from '@ember-data/store';

interface Args {
  accordion: any;
  livePersonConfig: LivePersonConfiguration;
}

export default class StandaloneApiLivePersonConnection extends Component<Args> {
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare intl: IntlService;

  @tracked isDrawerOpen = false;
  @tracked customAuthenticationTokens: Token[] = [];
  @tracked selectedTokenId = this.args.livePersonConfig.tokenId;
  @tracked originalTokenId = this.args.livePersonConfig.tokenId;
  @tracked isSavingToken = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.loadCustomAuthenticationTokens).perform();
  }

  get tokenList() {
    return [
      {
        items: this.customAuthenticationTokens
          .filter((token) => token.tokenType === 'user')
          .map((token) => ({
            value: token.id,
            text: token.name,
          })),
      },
    ];
  }

  get selectedToken() {
    return this.customAuthenticationTokens?.find((token) => token.id === this.selectedTokenId);
  }

  get tokenFooterActions() {
    return [
      {
        text: this.intl.t('standalone.deploy.api.live_person.user_token_mapping.link_user_token'),
        icon: 'new',
        action: () =>
          safeWindowOpen(this.router.urlFor('apps.app.settings.app-settings.authentication.new')),
      },
    ];
  }

  get hasTokenChanges() {
    return this.selectedTokenId !== this.originalTokenId;
  }

  @action openDrawer() {
    this.isDrawerOpen = true;
  }

  @action closeDrawer() {
    this.isDrawerOpen = false;
  }

  @action closeOnSave() {
    this.isDrawerOpen = false;
  }

  @action onTokenSelected(tokenId: string) {
    this.selectedTokenId = tokenId;
  }

  @action async saveToken() {
    this.isSavingToken = true;

    try {
      let configId = this.args.livePersonConfig.id;
      let tokenId = this.selectedTokenId || null;

      await post(`/ember/standalone/live_person_configurations/${configId}/update_token`, {
        token_id: tokenId,
      });

      this.args.livePersonConfig.tokenId = tokenId;
      this.originalTokenId = this.selectedTokenId;
    } catch (error) {
      console.error('Error updating token:', error);
    } finally {
      this.isSavingToken = false;
    }
  }

  @action cancelTokenChanges() {
    this.selectedTokenId = this.originalTokenId;
  }

  @task({ drop: true })
  *loadCustomAuthenticationTokens(): TaskGenerator<void> {
    this.customAuthenticationTokens = yield this.store.query('custom-authentication/token', {
      with_app_package_tokens: false,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Api::LivePersonConnection': typeof StandaloneApiLivePersonConnection;
    'standalone/api/live-person-connection': typeof StandaloneApiLivePersonConnection;
  }
}
